
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from "lucide-react";
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { updatePassword, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localLoading, setLocalLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const handleAuthCallback = async () => {
      // Check if this is a password reset
      const type = searchParams.get('type');
      if (type === 'recovery') {
        setIsPasswordReset(true);
        setLocalLoading(false);
        return;
      }
      
      try {
        // Check if we have a session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          throw sessionError;
        }

        if (session) {
          // Determine where to redirect based on user role
          const role = session.user?.user_metadata?.role;
          let redirectTo = '/dashboard'; // Default redirect
          
          if (role === 'studio_owner') {
            redirectTo = '/studio-dashboard';
          } else if (role === 'staff') {
            redirectTo = '/staff-dashboard';
          } else if (role === 'admin') {
            redirectTo = '/admin';
          }
          
          navigate(redirectTo, { replace: true });
        } else {
          // No session found, redirect to login
          navigate('/login', { replace: true });
        }
      } catch (error: any) {
        console.error('Error in auth callback:', error);
        setError(error.message);
        toast({
          title: "Authentication Error",
          description: error.message,
          variant: "destructive",
        });
      } finally {
        setLocalLoading(false);
      }
    };

    handleAuthCallback();
  }, [navigate, searchParams, toast]);

  const handlePasswordReset = async () => {
    // Validate password strength
    const hasMinLength = password.length >= 8;
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    if (!hasMinLength || !hasNumber || !hasSpecialChar) {
      toast({
        title: "Passwort zu schwach",
        description: "Dein Passwort muss mindestens 8 Zeichen lang sein und mindestens eine Zahl sowie ein Sonderzeichen enthalten.",
        variant: "destructive",
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: "Passwörter stimmen nicht überein",
        description: "Bitte stelle sicher, dass deine Passwörter übereinstimmen.",
        variant: "destructive",
      });
      return;
    }

    try {
      await updatePassword(password);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Error resetting password:', error);
    }
  };

  if (localLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-chrona-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 size={48} className="animate-spin text-chrona-accent" />
          <h1 className="text-2xl font-bold text-chrona-text">Authentifizierung wird verarbeitet...</h1>
          <p className="text-chrona-gray-300">Bitte warten Sie, Sie werden weitergeleitet.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-chrona-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Fehler bei der Authentifizierung</CardTitle>
            <CardDescription>Es ist ein Fehler aufgetreten.</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-destructive">{error}</p>
          </CardContent>
          <CardFooter>
            <Button onClick={() => navigate('/login', { replace: true })}>
              Zurück zum Login
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (isPasswordReset) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-chrona-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Passwort zurücksetzen</CardTitle>
            <CardDescription>Bitte gib dein neues Passwort ein.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">Neues Passwort</Label>
              <PasswordInput
                id="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
              <p className="text-xs text-muted-foreground">
                Mindestens 8 Zeichen, eine Zahl und ein Sonderzeichen
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Passwort bestätigen</Label>
              <PasswordInput
                id="confirm-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>
          </CardContent>
          <CardFooter>
            <Button 
              onClick={handlePasswordReset}
              disabled={authLoading}
              className="w-full"
            >
              {authLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Bitte warten...
                </>
              ) : (
                'Passwort speichern'
              )}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return null; // This should never be reached, but TypeScript requires a return
}
